import {
  useEffect, useState, memo,
  Dispatch, SetStateAction
} from "react";
import classNames from "classnames";
import styles from "./ChatItem.module.scss";

import {
   Tooltip } from "components";
import { WordHighlighter } from "./WordHighlighter";

import {
  NewImageIcon,
  NewChatIcon,
  ImageGenerationIcon,
  VideoIcon,
  AudioIcon
} from "../icons";

import {
  IChat,
  EChatType
} from "redux/actions";

import { useSelector } from "redux/hooks";
import { NewDocumentIcon } from "../icons/NewDocumentIcon";
import { useSidebar } from "hooks/services/ReSizeSidebar/ReSizeSidebar";
import { removeQuotes } from "utils/functions";

interface IProps {
  chatItem: IChat;
  selectedItem?: IChat;
  onSelectChat?: (itemId: IChat | undefined, folder_id: number) => void;
  searchQuery: string;
  handleSelectId: (folder_id: number | string, id: number) => void;
  selectedChatIds: Record<string | number, number[]>;
  setSelectedChatIds: Dispatch<SetStateAction<Record<string | number, number[]>>>;
  onStartNewChat?: ({ toChat }: { toChat: boolean }) => void;
  setSelectedItem: (chatItem: IChat | undefined) => void;
  folder_id: number | string;
  setManuallySelectedChatIds: Dispatch<SetStateAction<Record<string | number, number[]>>>;
}

export const ChatItem = memo(
  ({ chatItem, selectedItem, setSelectedItem, onSelectChat, searchQuery,
      folder_id, 
    }: IProps) => {
    const themeMode = useSelector((state) => state.authReducer.theme);
    const { chathistory } = useSelector((state) => state.chatReducer);
    const { Folders } = useSelector((state) => state.chatFolderReducer);
    const { sidebarWidth } = useSidebar();

    const isActive: boolean = selectedItem?.id === chatItem.id;
    
    const [rightClickedId, setRightClickedId] = useState<number | null>(null);

    useEffect(() => {
      const currentPath = window.location.pathname;
      const pathSegments = currentPath.split('/');
      let chatId;
      if (pathSegments.length === 5)
        chatId = pathSegments[pathSegments.length - 2];
      else
        chatId = pathSegments[pathSegments.length - 1];

      if (chatItem.id === Number(chatId)) {
        setSelectedItem(chatItem);
      }
      else if (chatId === 'new') { setSelectedItem(undefined) }
    }, [window.location.pathname, chathistory, Folders]);
 
    const handleRightClick = () => {
      setRightClickedId(chatItem.id); // Set the right-clicked chat ID
    };

    const getChatTypeIcon = (chatType: EChatType) => {
      const icons: Record<EChatType, JSX.Element> = {
        text: <NewChatIcon />,
        document: <NewDocumentIcon />,
        image_chat: <NewImageIcon />,
        image: <ImageGenerationIcon />,
        video: <VideoIcon />,
        audio: <AudioIcon />,
      };

      return icons[chatType] || null;
    };

    return (
      <Tooltip
        control={
          <a href={`/chat/history/${rightClickedId}/${folder_id}`} onClick={(e) => e.preventDefault()} data-testid="chat-link">
            <div
              className={classNames("font-inter",styles.itemContainer, {
                [styles.itemContainerDark]: themeMode === "dark",
                [styles.itemContainerLight]: themeMode === "light",
                [styles.active]: isActive,
              })}
              onClick={() => {
                if (typeof folder_id === 'number' && !isNaN(folder_id)) {
                  onSelectChat?.(chatItem, folder_id);
                } else {
                  onSelectChat?.(chatItem, chatItem?.folder?.id ?? 0); 
                }
              }
              }

              onContextMenu={handleRightClick}
              data-testid='select-chat'
            >
              <div className={styles.check}>
                <div className={styles.actionIcon} style={{ marginTop: chatItem.chat_type === "text" ? "3px" : "", marginLeft: chatItem.chat_type === "document" ? "1.5px" : "" }}>
                  {getChatTypeIcon(chatItem.chat_type)}
                </div>

              </div>
              <div
                className={classNames(styles.itemMsgActionContainer, {
                  [styles.itemMsgActionContainerLight]: themeMode === "light",
                  [styles.itemMsgActionContainerDark]: themeMode === "dark",
                  [styles.active]: isActive,
                })}
              >
                <div className={classNames(styles.itemMessageContainer)}>
                    <div
                      className={classNames(styles.itemText, {
                        [styles.isActive]: isActive,
                        [styles.itemField]: sidebarWidth <= 325,
                        [styles.itemFieldAdjust]: sidebarWidth <= 350 && sidebarWidth > 325,
                      })}
                    >
                      <WordHighlighter
                        searchQuery={searchQuery}
                        text={removeQuotes(chatItem.topic) || "New chat"}
                        excerptLength={160}
                      />
                    </div>
                </div>
              </div>
            </div>
          </a>
        }
        placement="right"
        theme="light"
      >
        {removeQuotes(chatItem.topic) || "New Chat"}
      </Tooltip>
    );
  }
);
